import { FC, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { useTheme, css } from 'styled-components';
import { LayoutConst } from "core/modules";
import {
  FlexBox,
  useIsMobile,
  Fonts,
  Colors,
} from '@brg/ui';
import ArrowTopLarge from "../../icons/ArrowTopLarge";
import OnlinesLogoBig from 'shared/assets/logos/OnlinesLogo_big';
import InstagramOnlinesIcon from 'shared/icons/onlines/InstagramOnlinesIcon';
import FacebookOnlinesIcon from 'shared/icons/onlines/FacebookOnlinesIcon';
import YoutubeOnlinesIcon from 'shared/icons/onlines/YouTubeOnlinesIcon';
import WPOnlinesIcon from 'shared/icons/onlines/WPOnlinesIcon';
import useQueryState from 'shared/hooks/useQueryState';
import { BLanguageSelectionComponentOnlines } from 'core/components/BLanguageSelectionComponentOnlines';
import { SaveUserLang } from '../__ADDONS__/__LANG__/Utils/GetUserLang';

interface Props {
  isMobile?: boolean;
}

// export const ANCHOR_ONLINES_LINK = `/communication-design-basic-level#form`;
export const ANCHOR_ONLINES_LINK = `/join`;

const DefaultFooterWrapper = styled(FlexBox)`
  background: ${(p) => p.theme.footerView.backgroundColor};
  width: 100%;
  min-height: 280px;
  z-index: 1;
  position: relative;
  background: white;
  box-shadow: 0px 0px 30px rgba(0, 0, 0, 0.1);
  border-radius: 48px 48px 0px 0px;
  overflow: hidden;

  //   &.white-theme {
  //     background: white;
  //   }

  @media (max-width: ${LayoutConst.screen.l_12}px) {
    padding-left: 16px;
    padding-right: 16px;
  }

  @media (max-width: ${LayoutConst.screen.s}px) {
    padding-left: 16px;
    padding-right: 16px;
    min-height: 360px;
  }
`;

const Logo = styled.div<Props>`
  color: ${(p) => p.theme.navigationBar.navBarCreate.logoFillColor.second};
  font-weight: bold;
  font-size: 24px;
  line-height: 15px;
  @media (max-width: 940px) {
    ${Fonts.Inter.textBold};
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
  }
`;
const DefaultFooterContent = styled(FlexBox)`
  margin: 0 auto;
  width: 1152px;
  display: block;
`;

const NavItemForeignOuter = styled.div`
  @media (max-width: 940px) {
    padding-bottom: 15px;
    padding-right: 10px;
  }
`;

const DefaultFooterItemsWrapper = styled(FlexBox)`
  width: 100%;
  margin-top: 72px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;

  @media (max-width: 940px) {
    margin-top: 29px;
  }

  @media (max-width: 720px) {
    height: 128px;
  }
`;

const DefaultFooterItems = styled(FlexBox)<Props>`
  display: flex;
  flex-direction: row;

  ${p => p?.isMobile ? css`
    flex-wrap: wrap;
  ` : null};

  height: 64px;

  span {
    margin-left: 24px;
  }

  @media (max-width: 940px) {
    height: auto;
    width: 100%;
    justify-content: space-between;
  }
`;

const Text = styled.div`
  font-weight: 500;
  font-style: normal;
  color: rgba(0, 0, 0, 1);
  font-family: Montserrat;
  font-variation-settings: 'wght' 500;
  font-size: 14.5px;
  line-height: 18px;
  letter-spacing: -0.1px;
`;

const LINK = styled.a`
  text-decoration: none;
  font-weight: 500;
  font-style: normal;
  color: rgba(144, 144, 144, 1);
  font-family: Montserrat;
  font-size: 14.5px;
  line-height: 18px;
  letter-spacing: -0.1px;

  &:hover {
    text-decoration: none;
    color: #2D2F33;
  }
`;

const ButtonTop = styled.button<{ onlines?: boolean }>`
  border-style: none;
  background: transparent;
  box-sizing: content-box;
  cursor: pointer;
  display: flex;
  flex-flow: row;
  place-items: center;
  color: rgba(144, 144, 144, 1);
  font-weight: 500;
  font-style: normal;
  font-family: Montserrat;
  font-variation-settings: 'wght' 500;
  font-size: 18px;
  line-height: 18px;
  letter-spacing: -0.1px;

  & svg {
    circle {
      stroke: ${({ theme }) => theme.btns.hollow.border};
      display: none;
    }

    path {
      fill: ${Colors.galBlack47};
    }

    &:active {
      circle {
        stroke: ${({ theme }) => theme.btns.hollow.borderActive};
      }
    }
  }
`;

export const ApplyBtn = styled.a<{ size?: 'big' | 'small' | 'medium', lang?: string }>`
  background-color: rgb(0, 0, 0);
  border-radius: 16px;
  border-width: 0px;
  border-color: rgb(0, 0, 0);
  font-family: Montserrat;
  font-variation-settings: 'wght' 600;
  font-weight: 600;
  font-style: normal;
  color: rgb(255, 255, 255) !important;
  cursor: pointer;
  font-size: 14px;
  line-height: 22px;
  letter-spacing: 0px;
  white-space: pre;
  width: 128px;
  min-height: 37px;
  text-decoration: none;
  display: flex;
  place-items: center;
  justify-content: center;
  flex-shrink: 0;

  ${(p) => p?.size === 'big' && p.lang === 'en' ? css`
    width: 210px;
    min-height: 50px !important;
    font-weight: 600;
    font-size: 20px;
    line-height: 24px;
  ` : css`
    width: 205px;
    min-height: 50px !important;
    font-weight: 600;
    font-size: 20px;
    line-height: 24px;
  `}

  ${p => p?.size === 'small' ? css`
    min-height: 28px !important;
    font-size: 13px;
    line-height: 18px;
  ` : null};

  @media (max-width: 940px) {
    border-radius: 12px;
    font-size: 12px;
    line-height: 22px;
    width: ${p => p.lang === 'en' ? '110px' : '110px'};
    min-height: 40px;

  }

  &:hover {
    color: rgb(255, 255, 255);
  }
`;

function toTheTopScroll() {
  window.scrollTo({
    top: 0,
    behavior: "smooth",
  });
}


type PropsT = {
  className?: string;
};

const OnlinesFooter: FC<PropsT> = () => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const [{}, setQueryParams] = useQueryState();

  const isMobile = useIsMobile();

  const onOnlinesLangSwitch = useCallback((lang: string) => {
    // @ts-ignore
    SaveUserLang(lang);
    setQueryParams({ lang });
    i18n.changeLanguage(lang, () =>  window.location.reload());
  }, [setQueryParams, i18n]);

  const [{ lang }] = useQueryState();

  const scrollToTopButton = (
    <ButtonTop onClick={toTheTopScroll}>
      <div>{t('onlinesNav.footer.back')}</div>
      <ArrowTopLarge/>
    </ButtonTop>
  );

  useEffect(() => {
    if (isMobile) {
      i18n.changeLanguage(lang)
    }
  }, [i18n, lang, isMobile])

  return (
    <div id="footer-portal">
      <DefaultFooterWrapper style={{ border: theme.footer.border }}>
        <DefaultFooterContent>
          <DefaultFooterItemsWrapper>
            <DefaultFooterItems isMobile={isMobile}>
              {isMobile && (
                <FlexBox directionMobile='row' justifyMobile='flex-end' widthMobile={'100%'} style={{ placeItems: 'center' }}>
                  <BLanguageSelectionComponentOnlines onLangSelect={onOnlinesLangSwitch} />
                  {scrollToTopButton}
                </FlexBox>
              )}
              <FlexBox offsetRight={100} direction='column' directionMobile='column' justify='start'>
                <FlexBox offsetBottom={18}>
                  <OnlinesLogoBig/>
                </FlexBox>
                <FlexBox directionMobile='column' direction='row'>
                  <ApplyBtn lang={i18n.language} href="https://calendly.com/onlinesdesign/application-q-a?back=1" target="_blank" size={isMobile ? 'small' : 'big'}>{t('onlinesNav.btns.apply')}</ApplyBtn>
                  <FlexBox offsetTopMobile={16} gx={24} gxMobile={24} style={{ placeItems: 'center', marginLeft: isMobile ? 0 : '24px' }}>
                    <a href='https://wa.me/905355422782' >
                      <WPOnlinesIcon />
                    </a>
                    <a href='https://www.instagram.com/onlines_designschool/'>
                      <InstagramOnlinesIcon />
                    </a>
                    <a href='https://www.facebook.com/profile.php?id=100087517002925'>
                      <FacebookOnlinesIcon />
                    </a>
                    <a href='https://www.youtube.com/channel/UCVF6F8UPPfWi1Gocnha3rlg'>
                      <YoutubeOnlinesIcon />
                    </a>
                  </FlexBox>
                </FlexBox>
              </FlexBox>
              <FlexBox style={{ maxWidth: isMobile ? 'calc(320px - 32px)' : 'inherit' }} direction='column'
                       offsetTopMobile={24} wrapMobile='wrap' gy={20} width='332px' widthMobile={'calc(100% - 32px)'}>
                <Text>{t('onlinesNav.footer.text')}</Text>
                <FlexBox gxMobile={16} direction="column" directionMobile="row">
                  <LINK href="mailto:hello@onlinesdesign.com">hello@onlinesdesign.com</LINK>
                  <FlexBox gx={16} offsetTop={20} offsetTopMobile={'0'}>
                    <LINK href="https://onlinesdesign.com/legals/license" target="_blank">{t('onlinesNav.footer.legals')}</LINK>
                    <LINK href="https://onlinesdesign.com/legals/privacy" target="_blank">{t('onlinesNav.footer.privacy')}</LINK>
                  </FlexBox>
              </FlexBox>
                </FlexBox>
            </DefaultFooterItems>
            {!isMobile && scrollToTopButton}
          </DefaultFooterItemsWrapper>
        </DefaultFooterContent>
      </DefaultFooterWrapper>
    </div>
  );
};

export default OnlinesFooter;
import { FC, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { useTheme, css } from 'styled-components';
import { LayoutConst } from "core/modules";
import {
  FlexBox,
  useIsMobile,
  Fonts,
  Colors,
} from '@brg/ui';
import ArrowTopLarge from "../../icons/ArrowTopLarge";
import OnlinesLogoBig from 'shared/assets/logos/OnlinesLogo_big';
import InstagramOnlinesIcon from 'shared/icons/onlines/InstagramOnlinesIcon';
import FacebookOnlinesIcon from 'shared/icons/onlines/FacebookOnlinesIcon';
import YoutubeOnlinesIcon from 'shared/icons/onlines/YouTubeOnlinesIcon';
import WPOnlinesIcon from 'shared/icons/onlines/WPOnlinesIcon';
import useQueryState from 'shared/hooks/useQueryState';
import { BLanguageSelectionComponentOnlines } from 'core/components/BLanguageSelectionComponentOnlines';
import { SaveUserLang } from '../__ADDONS__/__LANG__/Utils/GetUserLang';

interface Props {
  isMobile?: boolean;
}

// export const ANCHOR_ONLINES_LINK = `/communication-design-basic-level#form`;
export const ANCHOR_ONLINES_LINK = `/join`;

const DefaultFooterWrapper = styled(FlexBox)`
  background: ${(p) => p.theme.footerView.backgroundColor};
  width: 100%;
  min-height: 280px;
  z-index: 1;
  position: relative;
  background: white;
  box-shadow: 0px 0px 30px rgba(0, 0, 0, 0.1);
  border-radius: 48px 48px 0px 0px;
  overflow: hidden;

  //   &.white-theme {
  //     background: white;
  //   }

  @media (max-width: ${LayoutConst.screen.l_12}px) {
    padding-left: 16px;
    padding-right: 16px;
  }

  @media (max-width: ${LayoutConst.screen.s}px) {
    padding-left: 16px;
    padding-right: 16px;
    min-height: 360px;
  }
`;

const Logo = styled.div<Props>`
  color: ${(p) => p.theme.navigationBar.navBarCreate.logoFillColor.second};
  font-weight: bold;
  font-size: 24px;
  line-height: 15px;
  @media (max-width: 940px) {
    ${Fonts.Inter.textBold};
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
  }
`;
const DefaultFooterContent = styled(FlexBox)`
  margin: 0 auto;
  width: 1152px;
  display: block;
`;

const NavItemForeignOuter = styled.div`
  @media (max-width: 940px) {
    padding-bottom: 15px;
    padding-right: 10px;
  }
`;

const DefaultFooterItemsWrapper = styled(FlexBox)`
  width: 100%;
  margin-top: 72px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;

  @media (max-width: 940px) {
    margin-top: 29px;
  }

  @media (max-width: 720px) {
    height: 128px;
  }
`;

const DefaultFooterItems = styled(FlexBox)<Props>`
  display: flex;
  flex-direction: row;

  ${p => p?.isMobile ? css`
    flex-wrap: wrap;
  ` : null};

  height: 64px;

  span {
    margin-left: 24px;
  }

  @media (max-width: 940px) {
    height: auto;
    width: 100%;
    justify-content: space-between;
  }
`;

const Text = styled.div`
  font-weight: 500;
  font-style: normal;
  color: rgba(0, 0, 0, 1);
  font-family: Montserrat;
  font-variation-settings: 'wght' 500;
  font-size: 14.5px;
  line-height: 18px;
  letter-spacing: -0.1px;
`;

const LINK = styled.a`
  text-decoration: none;
  font-weight: 500;
  font-style: normal;
  color: rgba(144, 144, 144, 1);
  font-family: Montserrat;
  font-size: 14.5px;
  line-height: 18px;
  letter-spacing: -0.1px;

  &:hover {
    text-decoration: none;
    color: #2D2F33;
  }
`;

const ButtonTop = styled.button<{ onlines?: boolean }>`
  border-style: none;
  background: transparent;
  box-sizing: content-box;
  cursor: pointer;
  display: flex;
  flex-flow: row;
  place-items: center;
  color: rgba(144, 144, 144, 1);
  font-weight: 500;
  font-style: normal;
  font-family: Montserrat;
  font-variation-settings: 'wght' 500;
  font-size: 18px;
  line-height: 18px;
  letter-spacing: -0.1px;

  & svg {
    circle {
      stroke: ${({ theme }) => theme.btns.hollow.border};
      display: none;
    }

    path {
      fill: ${Colors.galBlack47};
    }

    &:active {
      circle {
        stroke: ${({ theme }) => theme.btns.hollow.borderActive};
      }
    }
  }
`;

export const ApplyBtn = styled.a<{ size?: 'big' | 'small' | 'medium', lang?: string }>`
  background-color: rgb(0, 0, 0);
  border-radius: 16px;
  border-width: 0px;
  border-color: rgb(0, 0, 0);
  font-family: Montserrat;
  font-variation-settings: 'wght' 600;
  font-weight: 600;
  font-style: normal;
  color: rgb(255, 255, 255) !important;
  cursor: pointer;
  font-size: 14px;
  line-height: 22px;
  letter-spacing: 0px;
  white-space: pre;
  width: 128px;
  min-height: 37px;
  text-decoration: none;
  display: flex;
  place-items: center;
  justify-content: center;
  flex-shrink: 0;

  ${(p) => p?.size === 'big' && p.lang === 'en' ? css`
    width: 210px;
    min-height: 50px !important;
    font-weight: 600;
    font-size: 20px;
    line-height: 24px;
  ` : css`
    width: 205px;
    min-height: 50px !important;
    font-weight: 600;
    font-size: 20px;
    line-height: 24px;
  `}

  ${p => p?.size === 'small' ? css`
    min-height: 28px !important;
    font-size: 13px;
    line-height: 18px;
  ` : null};

  @media (max-width: 940px) {
    border-radius: 12px;
    font-size: 12px;
    line-height: 22px;
    width: ${p => p.lang === 'en' ? '110px' : '110px'};
    min-height: 40px;

  }

  &:hover {
    color: rgb(255, 255, 255);
  }
`;

function toTheTopScroll() {
  window.scrollTo({
    top: 0,
    behavior: "smooth",
  });
}


type PropsT = {
  className?: string;
};

const OnlinesFooter: FC<PropsT> = () => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const [{}, setQueryParams] = useQueryState();

  const isMobile = useIsMobile();

  const onOnlinesLangSwitch = useCallback((lang: string) => {
    // @ts-ignore
    SaveUserLang(lang);
    setQueryParams({ lang });
    i18n.changeLanguage(lang, () =>  window.location.reload());
  }, [setQueryParams, i18n]);

  const [{ lang }] = useQueryState();

  const scrollToTopButton = (
    <ButtonTop onClick={toTheTopScroll}>
      <div>{t('onlinesNav.footer.back')}</div>
      <ArrowTopLarge/>
    </ButtonTop>
  );

  useEffect(() => {
    if (isMobile) {
      i18n.changeLanguage(lang)
    }
  }, [i18n, lang, isMobile])

  return (
    <div id="footer-portal">
      <DefaultFooterWrapper style={{ border: theme.footer.border }}>
        <DefaultFooterContent>
          <DefaultFooterItemsWrapper>
            <DefaultFooterItems isMobile={isMobile}>
              {isMobile && (
                <FlexBox directionMobile='row' justifyMobile='flex-end' widthMobile={'100%'} style={{ placeItems: 'center' }}>
                  <BLanguageSelectionComponentOnlines onLangSelect={onOnlinesLangSwitch} />
                  {scrollToTopButton}
                </FlexBox>
              )}
              <FlexBox offsetRight={100} direction='column' directionMobile='column' justify='start'>
                <FlexBox offsetBottom={18}>
                  <OnlinesLogoBig/>
                </FlexBox>
                <FlexBox directionMobile='column' direction='row'>
                  <ApplyBtn lang={i18n.language} href="https://calendly.com/onlinesdesign/application-q-a?back=1" target="_blank" size={isMobile ? 'small' : 'big'}>{t('onlinesNav.btns.apply')}</ApplyBtn>
                  <FlexBox offsetTopMobile={16} gx={24} gxMobile={24} style={{ placeItems: 'center', marginLeft: isMobile ? 0 : '24px' }}>
                    <a href='https://wa.me/905355422782' >
                      <WPOnlinesIcon />
                    </a>
                    <a href='https://www.instagram.com/onlines_designschool/'>
                      <InstagramOnlinesIcon />
                    </a>
                    <a href='https://www.facebook.com/profile.php?id=100087517002925'>
                      <FacebookOnlinesIcon />
                    </a>
                    <a href='https://www.youtube.com/channel/UCVF6F8UPPfWi1Gocnha3rlg'>
                      <YoutubeOnlinesIcon />
                    </a>
                  </FlexBox>
                </FlexBox>
              </FlexBox>
              <FlexBox style={{ maxWidth: isMobile ? 'calc(320px - 32px)' : 'inherit' }} direction='column'
                       offsetTopMobile={24} wrapMobile='wrap' gy={20} width='332px' widthMobile={'calc(100% - 32px)'}>
                <Text>{t('onlinesNav.footer.text')}</Text>
                <FlexBox gxMobile={16} direction="column" directionMobile="row">
                  <LINK href="mailto:hello@onlinesdesign.com">hello@onlinesdesign.com</LINK>
                  <FlexBox gx={16} offsetTop={20} offsetTopMobile={'0'}>
                    <LINK href="https://onlinesdesign.com/legals/license" target="_blank">{t('onlinesNav.footer.legals')}</LINK>
                    <LINK href="https://onlinesdesign.com/legals/privacy" target="_blank">{t('onlinesNav.footer.privacy')}</LINK>
                  </FlexBox>
              </FlexBox>
                </FlexBox>
            </DefaultFooterItems>
            {!isMobile && scrollToTopButton}
          </DefaultFooterItemsWrapper>
        </DefaultFooterContent>
      </DefaultFooterWrapper>
    </div>
  );
};

export default OnlinesFooter;
